import { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/contexts/AuthContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ApiDocs from "./pages/ApiDocs";
import ProtectedRoute from "./components/ProtectedRoute";
import { isApiError } from "@/services/api/apiUtils";
import * as Sentry from "@sentry/react";
import { BotDetectionProvider } from "@/security/botDetection";

// Public pages
import Landing from "@/pages/Landing";
import Index from "@/pages/Index";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import SetPassword from "@/pages/auth/SetPassword";
import MagicLinkCallback from "@/pages/auth/MagicLinkCallback";
import NotFound from "@/pages/NotFound";

// Buyer pages
import BuyerDashboard from "@/pages/buyer/Dashboard";
import BuyerSearch from "@/pages/buyer/Search";
import BuyerDocumentRequest from "@/pages/buyer/DocumentRequest";
import BuyerPayment from "@/pages/buyer/Payment";
import BuyerConfirmation from "@/pages/buyer/Confirmation";
import BuyerOrders from "@/pages/buyer/Orders";
import BuyerOrderDetail from "@/pages/buyer/OrderDetail";

// Route collections
import backofficeRoutes from "./routes/backofficeRoutes";

// Create a client with custom configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on permission denied (403) or unauthorized (401) errors
        // These are client errors that won't be resolved by retrying
        if (isApiError(error)) {
          if (error.status === 403 || error.status === 401) {
            return false;
          }
        }
        // Default retry behavior: retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry mutations on client errors (4xx) except for request timeouts (408)
        // Permission denied (403), unauthorized (401), and other client errors won't be resolved by retrying
        if (isApiError(error)) {
          if (
            error.status >= 400 &&
            error.status < 500 &&
            error.status !== 408
          ) {
            return false;
          }
        }
        // Default retry behavior for mutations: retry up to 1 time for server errors (5xx)
        return failureCount < 1;
      },
    },
  },
});

// Route debugger component
const RouteDebugger = () => {
  const location = useLocation();

  useEffect(() => {
    console.log("Current route:", location.pathname);
    console.log("Location state:", location.state);
    console.log("Location hash:", location.hash);
    console.log("Location search:", location.search);
  }, [location]);

  return null;
};

// Password Reset Hash Checker - Redirect if access_token is in hash
const PasswordResetChecker = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip if we're already on an auth callback page
    if (
      location.pathname === "/auth/set-password" ||
      location.pathname === "/auth/magic-link"
    ) {
      return;
    }

    // Check for hash with access_token (Supabase auth redirect format)
    if (location.hash && location.hash.includes("access_token=")) {
      // Determine the type of authentication
      const isMagicLink = location.hash.includes("type=magiclink");
      const isRecovery = location.hash.includes("type=recovery");
      const isInvite = location.hash.includes("type=invite");

      if (isRecovery || isInvite) {
        console.log(
          "App detected recovery/invite access_token in hash, redirecting to set-password"
        );
        navigate("/auth/set-password", { replace: true });
      } else if (isMagicLink) {
        console.log(
          "App detected magic link access_token in hash, redirecting to magic-link callback"
        );
        navigate("/auth/magic-link", { replace: true });
      } else {
        // Default to magic link for unknown types with access_token
        console.log(
          "App detected unknown auth type with access_token, treating as magic link"
        );
        navigate("/auth/magic-link", { replace: true });
      }
    }
  }, [location, navigate]);

  return null;
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function App() {
  const location = useLocation();

  // Define routes that require platform roles
  const platformOnlyRoutes = ["/cms", "/settings", "/queue-monitor"];

  useEffect(() => {
    console.log("App component mounted");
    console.log("Available routes:", [
      "/",
      "/index",
      "/auth/login",
      "/auth/forgot-password",
      "/auth/set-password",
      "/auth/magic-link",
      "/buyer/dashboard",
      "/buyer/search",
      "/buyer/document-request",
      "/buyer/payment",
      "/buyer/confirmation",
      "/buyer/orders",
      "/buyer/orders/:id",
      "/api-docs",
      "/*",
      ...backofficeRoutes.map((route) => `/backoffice${route.path}`),
    ]);

    // Check if we're coming from a password reset link with hash parameters
    if (
      location.hash &&
      location.hash.includes("access_token=") &&
      location.pathname !== "/auth/set-password"
    ) {
      console.log(
        "App detected access_token in hash, should redirect to set-password"
      );
    }

    // Note: Document queue processing happens server-side only via edge functions
    // No client-side polling is needed - the edge function handles all message processing
    console.log(
      "Document queue processing is handled server-side via edge functions"
    );
  }, [location]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BotDetectionProvider>
          <RouteDebugger />
          <PasswordResetChecker />
          <Toaster />
        <SentryRoutes>
          {/* Public Routes - Redirect root to backoffice dashboard */}
          <Route path="/" element={<Navigate to="/backoffice/dashboard" />} />
          <Route path="/index" element={<Index />} />

          {/* Auth routes with proper paths */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/login" element={<Navigate to="/auth/login" />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/forgot-password"
            element={<Navigate to="/auth/forgot-password" />}
          />

          {/* Password reset routes - handle both direct paths and external verification */}
          <Route path="/auth/set-password" element={<SetPassword />} />
          <Route path="/auth/v1/verify" element={<SetPassword />} />

          {/* Magic link authentication callback */}
          <Route path="/auth/magic-link" element={<MagicLinkCallback />} />

          {/* Backoffice Routes - Now with authentication required and platform role protection for specific routes */}
          {backofficeRoutes.map((route) => {
            const requiresPlatformRole = platformOnlyRoutes.includes(
              route.path
            );
            return (
              <Route
                key={route.path}
                path={`/backoffice${route.path}`}
                element={
                  <ProtectedRoute
                    requireAuth={true}
                    requirePlatformRole={requiresPlatformRole}
                  >
                    {route.element}
                  </ProtectedRoute>
                }
              />
            );
          })}

          {/* Buyer Routes - Protected */}
          <Route
            path="/buyer/dashboard"
            element={
              <ProtectedRoute>
                <BuyerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/search"
            element={
              <ProtectedRoute>
                <BuyerSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/document-request"
            element={
              <ProtectedRoute>
                <BuyerDocumentRequest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/payment"
            element={
              <ProtectedRoute>
                <BuyerPayment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/confirmation"
            element={
              <ProtectedRoute>
                <BuyerConfirmation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/orders"
            element={
              <ProtectedRoute>
                <BuyerOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/orders/:id"
            element={
              <ProtectedRoute>
                <BuyerOrderDetail />
              </ProtectedRoute>
            }
          />

          {/* API Docs Route */}
          <Route path="/api-docs" element={<ApiDocs />} />

          {/* Not Found Route should be the last one */}
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
        </BotDetectionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
